import * as React from 'react';
import LoadingOverlay from '../../../common/LoadingOverlay';
import UnitTypeRow from './UnitTypeRow';
import FormValidationDisplay from '../../../common/form/ValidationDisplay';
import Button from '../../../common/form/Button';
import { ApiResult } from '../../../../api/Shared';
import {
    UnitType,
    getUnitTypes,
    deleteUnitType,
    updateUnitType,
    createUnitType,
    UnitTypeUpdate
} from '../../../../api/Property';
import { Roles } from './../../../../utilities/Enums';
import * as identity from './../../../../utilities/identity';
import {userIsCompanyManageUser,userIsPropertyManageUser,userIsSystemManageUser} from '../../../../utilities/identity';

interface UnitTypeDictionary {
    [id: number]: {
        description: string;
        layout: string;
        pmsUnitId: string;
        beingEdited: boolean;
        previousDescription: string;
        previousLayout: string;
        previousPmsUnitId: string;
    };
}

interface State {
    unitTypes: UnitTypeDictionary;
    isLoading: boolean;
    errorMessage?: string;
}

interface Props {
    propertyId: number;
}

class UnitTypeTab extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            unitTypes: {},
            isLoading: true
        };

        this.handleUnitTypeUpdate = this.handleUnitTypeUpdate.bind(this);
        this.renderUnitType = this.renderUnitType.bind(this);
        this.loadData = this.loadData.bind(this);
        this.handleUnitTypeDelete = this.handleUnitTypeDelete.bind(this);
        this.handleToggleEdit = this.handleToggleEdit.bind(this);
        this.handleDescriptionChange = this.handleDescriptionChange.bind(this);
        this.handleLayoutChange = this.handleLayoutChange.bind(this);
        this.handleUnitTypeCreate = this.handleUnitTypeCreate.bind(this);
        this.handleApiResult = this.handleApiResult.bind(this);
    }

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (prevProps.propertyId !== this.props.propertyId) {
            this.loadData();
        }
    }

    render() {
        const role = identity.getMyRole();
        const isAdmin = role === Roles.SystemAdmin || role === Roles.CompanyAdmin || role === Roles.PropertyAdmin;
        const isUser = (
            userIsCompanyManageUser() ||
            userIsPropertyManageUser() ||
            userIsSystemManageUser()
            );
        const canNotEdit = isAdmin && isUser;
        return (
            <div>
                <LoadingOverlay show={this.state.isLoading} inBootstrapColumn={true} />
                <div className="card card-config">
                    <div className="card-header">
                        <div className="row">
                            <div className="font-weight-bold col">Unit Types</div>
                        </div>
                    </div>
                    <FormValidationDisplay errorMessage={this.state.errorMessage} />
                    <table className="table table-hover">
                        <tbody>
                            {this.state.unitTypes == null ||
                                Object.keys(this.state.unitTypes).map(this.renderUnitType)}
                        </tbody>
                    </table>
                </div>
                <div className="admin-btn">
                    <Button
                        text="Add Type"
                        addlClassName="btn-primary"
                        onClick={this.handleUnitTypeCreate}
                        disabled={canNotEdit}
                    />
                </div>
            </div>
        );
    }

    private loadData() {
        getUnitTypes(this.props.propertyId).then(result => {
            if (result.success && result.data !== undefined) {
                var unitTypeDict = result.data.reduce(
                    (dict: UnitTypeDictionary, unitType: UnitType) => {
                        dict[unitType.id] = {
                            description: unitType.description,
                            layout: unitType.layout,
                            beingEdited: false,
                            previousDescription: unitType.description,
                            previousLayout: unitType.layout,
                            previousPmsUnitId: unitType.pmsUnitId,
                            pmsUnitId: unitType.pmsUnitId
                        };
                        return dict;
                    },
                    {}
                );
                this.setState({
                    ...this.state,
                    unitTypes: unitTypeDict,
                    isLoading: false
                });
            }
        });
    }

    private renderUnitType(unitTypeIdString: string) {
        let unitTypeId = parseInt(unitTypeIdString, 10);
        return (
            <UnitTypeRow
                key={unitTypeId}
                pmsUnitId={this.state.unitTypes[unitTypeId].pmsUnitId}
                layout={this.state.unitTypes[unitTypeId].layout}
                onDelete={() => this.handleUnitTypeDelete(unitTypeId)}
                description={this.state.unitTypes[unitTypeId].description}
                onSubmit={() => this.handleUnitTypeUpdate(unitTypeId)}
                onDescriptionChanged={(description: string) =>
                    this.handleDescriptionChange(unitTypeId, description)
                }
                pmsUnitIdChange={(pmsUnitId: string)=>{
                    this.handlePmsUnitIdChange(unitTypeId, pmsUnitId)
                }}
                onLayoutChanged={(layout: string) =>
                    this.handleLayoutChange(unitTypeId, layout)
                }
                beingEdited={this.state.unitTypes[unitTypeId].beingEdited}
                onToggleEdit={() => this.handleToggleEdit(unitTypeId)}
            />
        );
    }

    private handleToggleEdit(unitTypeId: number) {
        let internalDict = this.state.unitTypes;
        if (
            internalDict[unitTypeId].beingEdited &&
            internalDict[unitTypeId].previousDescription === ''
        ) {
            // canceling an edit of a blank unit type
            // delete it instead
            return this.handleUnitTypeDelete(unitTypeId);
        }
        internalDict[unitTypeId].description = internalDict[unitTypeId].previousDescription;
        internalDict[unitTypeId].previousPmsUnitId = internalDict[unitTypeId].previousPmsUnitId;
        internalDict[unitTypeId].beingEdited = !internalDict[unitTypeId].beingEdited;
        this.setState({
            ...this.state,
            unitTypes: internalDict
        });
    }

    private handleDescriptionChange(unitTypeId: number, description: string) {
        let internalDict = this.state.unitTypes;
        internalDict[unitTypeId].description = description;
        this.setState({
            ...this.state,
            unitTypes: internalDict
        });
    }

    private handlePmsUnitIdChange(unitTypeId: number, pmsUnitId: string) {
        let internalDict = this.state.unitTypes;
        internalDict[unitTypeId].pmsUnitId = pmsUnitId;
        this.setState({
            ...this.state,
            unitTypes: internalDict
        });
    }
    
    private handleLayoutChange(unitTypeId: number, layout: string) {
        let internalDict = this.state.unitTypes;
        internalDict[unitTypeId].layout = layout;
        this.setState({
            ...this.state,
            unitTypes: internalDict
        });
    }

    private handleUnitTypeDelete(unitTypeId: number) {
        this.setState({
            ...this.state,
            isLoading: true,
            errorMessage: undefined
        });
        deleteUnitType(this.props.propertyId, unitTypeId).then(result =>
            this.handleApiResult(result)
        );
    }

    private handleUnitTypeUpdate(unitTypeId: number) {
        this.setState({
            ...this.state,
            isLoading: true,
            errorMessage: undefined
        });
        let updatedUnitType = {
            description: this.state.unitTypes[unitTypeId].description,
            layout: this.state.unitTypes[unitTypeId].layout,
            pmsUnitId: this.state.unitTypes[unitTypeId].pmsUnitId
        } as UnitTypeUpdate;
        if (updatedUnitType.description === '') {
            this.setState({
                ...this.state,
                isLoading: false,
                errorMessage: 'Please enter a description for the unit type'
            });
            return;
        }
        if (updatedUnitType.description === this.state.unitTypes[unitTypeId].previousDescription && updatedUnitType.layout === this.state.unitTypes[unitTypeId].previousLayout && updatedUnitType.pmsUnitId === this.state.unitTypes[unitTypeId].previousPmsUnitId) {
            // if no change, don't need to submit to API
            return this.handleToggleEdit(unitTypeId);
        }
        updateUnitType(this.props.propertyId, unitTypeId, updatedUnitType).then(result =>
            this.handleApiResult(result)
        );
    }

    private handleUnitTypeCreate() {
        this.setState({
            ...this.state,
            isLoading: true
        });
        let newUnitType = {
            description: 'New Unit Type',
            layout: '',
            pmsUnitId: ''
        } as UnitTypeUpdate;
        createUnitType(this.props.propertyId, newUnitType).then(result =>
            this.handleApiResult(result)
        );
    }

    private handleApiResult<T>(result: ApiResult<T>) {
        if (result.success) {
            this.loadData();
        } else {
            let errorMessage =
                result.error === undefined || result.error === ''
                    ? 'Error contacting server'
                    : result.error;
            this.setState({
                ...this.state,
                errorMessage: errorMessage,
                isLoading: false
            });
        }
    }
}

export default UnitTypeTab;



// WEBPACK FOOTER //
// ./src/components/pages/admin/editProperty/UnitTypeTab.tsx